import {
  ComputedQuery,
  FiltersMap,
  OptionalFiltersMap,
  PickerState,
} from '@wix/bex-core';
import { Revert } from '@wix/wix-ui-icons-common';
import React, { ReactElement } from 'react';
import { Box, Text, TextButton } from '@wix/design-system';
import {
  CollectionEmptyStateBase,
  CollectionEmptyStateBaseProps,
} from '../CollectionEmptyStateBase';

interface PickerEmptyStateProps<T, F extends FiltersMap = OptionalFiltersMap> {
  hasFilterDropdown?: boolean;
  state: PickerState<T, F>;
  renderEmptyState?: (params: {
    hasAvailableItems: boolean;
    query: ComputedQuery<F>;
  }) => ReactElement;
}
export const PickerEmptyState = <T, F extends FiltersMap>({
  state,
  hasFilterDropdown,
  renderEmptyState,
}: PickerEmptyStateProps<T, F>) => {
  const { collection, minContentHeight } = state;
  const {
    result: { originQuery, hasAvailableItems },
    translate: t,
  } = collection;

  const showFilterOnlyEmptyState = Boolean(
    hasFilterDropdown && collection.query.search.isEmpty,
  );

  const showFilterWithSearchEmptyState = Boolean(
    hasFilterDropdown && !collection.query.search.isEmpty,
  );

  const filterOnlyEmptyStateProps: CollectionEmptyStateBaseProps<T, F> = {
    state: collection,
    dataHook: 'picker-empty-state-only-filter',
    title: t('cairo.search.noresults.title'),
    subtitle: t('cairo.selectorModal.searchEmptyState.changeFilter.subtitle'),
  };

  const filterWithSearchEmptyStateProps: CollectionEmptyStateBaseProps<T, F> = {
    state: collection,
    dataHook: 'picker-empty-state-filter-with-search',
    title: t('cairo.search.nothingMatches.title', {
      term: collection.result.originQuery.rawSearch,
    }),
    subtitle: (
      <Box direction="vertical" gap="18px" align="center">
        <Text size="small">
          {t('cairo.selectorModal.searchEmptyState.subtitle')}
        </Text>
        <TextButton
          prefixIcon={<Revert />}
          onClick={() => collection.clearSearch()}
        >
          {t('cairo.emptyState.reset.button')}
        </TextButton>
      </Box>
    ),
  };

  const getEmptyStateConfig = (): CollectionEmptyStateBaseProps<T, F> => {
    if (showFilterOnlyEmptyState) {
      return filterOnlyEmptyStateProps;
    }
    if (showFilterWithSearchEmptyState) {
      return filterWithSearchEmptyStateProps;
    }
    return { state: collection, dataHook: 'picker-empty-state' };
  };

  return (
    <Box direction="vertical" overflow="auto">
      <Box
        align="center"
        verticalAlign="middle"
        paddingTop="SP2"
        paddingBottom="SP9"
        height="100%"
        minHeight={minContentHeight}
      >
        {renderEmptyState?.({
          hasAvailableItems,
          query: originQuery,
        }) ?? <CollectionEmptyStateBase {...getEmptyStateConfig()} />}
      </Box>
    </Box>
  );
};
